import { ThumbsDown, ThumbsUp } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * WasThisHelpful — bottom-of-article feedback Yes/No widget.
 *
 * Design plan reference,
 * section 22.3 (right-rail "Was this helpful?" block) and
 * section 25.5 (feedback endpoint is deferred to v2 — for now the
 * widget shows a local thank-you toast and the response is dropped).
 *
 * The component is intentionally tiny: it owns local UI state
 * (idle / submitting / submitted), accepts an `onSubmit` callback
 * the parent can wire to whatever sink it likes (POST to a v2
 * endpoint when one ships, or no-op for the v1 local toast), and
 * resets via the `topicId` prop changing (route change in the docs
 * site).
 *
 * Visual: two outline-style buttons (Yes / No), each with an icon.
 * On click, the chosen button stays highlighted, the other fades to
 * disabled. A short thank-you line replaces the "Was this helpful?"
 * label.
 *
 * `disabled` is provided for stories / SSR — when true the buttons
 * render in their idle styling but are not interactive.
 */

export type WasThisHelpfulValue = 'yes' | 'no'

export interface WasThisHelpfulProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSubmit'> {
  /**
   * Stable identifier for the article being rated. Changing it
   * resets the widget — the next article render starts fresh.
   */
  topicId: string
  /**
   * Fired when the user picks Yes / No. The Promise (if returned)
   * is awaited; while pending the widget shows a submitting state.
   * Errors are swallowed silently — the user already gave us their
   * answer locally; an analytics POST failure shouldn't show them
   * an error UI.
   */
  onSubmit?: (value: WasThisHelpfulValue) => void | Promise<void>
  /** Hard-disable both buttons. */
  disabled?: boolean
  /** Override the prompt text. Defaults to 'Was this helpful?'. */
  label?: string
  /** Override the thank-you text. Defaults to 'Thanks for the feedback.'. */
  thanksLabel?: string
}

export const WasThisHelpful = React.forwardRef<HTMLElement, WasThisHelpfulProps>(
  (
    {
      topicId,
      onSubmit,
      disabled,
      label = 'Was this helpful?',
      thanksLabel = 'Thanks for the feedback.',
      className,
      ...rest
    },
    ref,
  ) => {
    type State =
      | { kind: 'idle' }
      | { kind: 'submitting'; value: WasThisHelpfulValue }
      | { kind: 'submitted'; value: WasThisHelpfulValue }

    const [state, setState] = React.useState<State>({ kind: 'idle' })

    // Reset when the article changes so the widget restarts blank
    // on the next docs page.
    React.useEffect(() => {
      setState({ kind: 'idle' })
    }, [topicId])

    const handlePick = React.useCallback(
      async (value: WasThisHelpfulValue) => {
        if (state.kind !== 'idle' || disabled) return
        setState({ kind: 'submitting', value })
        try {
          await onSubmit?.(value)
        } catch {
          // Intentionally silent — we keep the local "thanks" UX
          // even if a downstream POST fails. The user fulfilled their
          // half of the interaction; a network blip is not their
          // problem.
        }
        setState({ kind: 'submitted', value })
      },
      [state.kind, disabled, onSubmit],
    )

    const submitted = state.kind === 'submitted'
    const submitting = state.kind === 'submitting'
    const picked = state.kind !== 'idle' ? state.value : undefined

    return (
      <section
        ref={ref}
        aria-label={label}
        className={cn('flex flex-col gap-2', className)}
        {...rest}
      >
        <div className="font-mono text-[9px] font-semibold uppercase tracking-[1.2px] text-ia-text-secondary">
          {submitted ? thanksLabel : label}
        </div>
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            disabled={disabled || submitting || submitted}
            aria-pressed={picked === 'yes' ? true : undefined}
            onClick={() => handlePick('yes')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-body-sm',
              'transition-[border-color,background-color,color] duration-100 motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
              picked === 'yes'
                ? 'border-[var(--color-ia-sidebar-active-border)] bg-ia-green-muted text-ia-green-text font-medium'
                : 'border-ia-border bg-card text-ia-text-secondary hover:border-ia-border-strong hover:text-ia-text',
              (disabled || submitted) && picked !== 'yes' && 'opacity-50',
            )}
          >
            <ThumbsUp size={14} aria-hidden="true" />
            <span>Yes</span>
          </button>
          <button
            type="button"
            disabled={disabled || submitting || submitted}
            aria-pressed={picked === 'no' ? true : undefined}
            onClick={() => handlePick('no')}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-body-sm',
              'transition-[border-color,background-color,color] duration-100 motion-reduce:transition-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
              picked === 'no'
                ? 'border-ia-border-strong bg-ia-surface-hover text-ia-text font-medium'
                : 'border-ia-border bg-card text-ia-text-secondary hover:border-ia-border-strong hover:text-ia-text',
              (disabled || submitted) && picked !== 'no' && 'opacity-50',
            )}
          >
            <ThumbsDown size={14} aria-hidden="true" />
            <span>No</span>
          </button>
        </div>
      </section>
    )
  },
)
WasThisHelpful.displayName = 'WasThisHelpful'
